"use client";

import { useCallback, useEffect, useState } from "react";
import {
  LuCalendar,
  LuChevronDown,
  LuCircleDollarSign,
  LuKeyRound,
} from "react-icons/lu";

import {
  FilterDate,
  FilterMultiUser,
  FilterNumeric,
  FiltersSet,
} from "@/components/shared/filter-sections";
import { PopoverFilterSortActions } from "@/components/shared/popover-filter-sort-actions";
import { Accordion } from "@/components/ui/accordion";
import { Button } from "@/components/ui/button";
import {
  PopoverDrawer,
  PopoverDrawerContent,
  PopoverDrawerTrigger,
  usePopoverDrawerContext,
} from "@/components/ui/popover-drawer";
import { type FilterRecord } from "@/contexts/filters-sort-context";
import type { PartialAccount } from "@/utils/types";

import { ActivityFilterFields } from "./constants";

export const TradeActivityFilterPopover = ({
  methods,
  filters,
  onResetFilters,
  onApplyFilters,
}: {
  methods: {
    findFilterByName: (name: string) => FilterRecord | undefined;
    updateFilter: (filter: FilterRecord, clearPrevFilters?: boolean) => void;
    removeFilter: <T extends string>(name: T) => void;
    isFilterActive: <T extends string>(name: T) => boolean;
    syncLastSavedFilters: () => void;
    setFiltersToLastSaved: () => void;
  };
  filters: FilterRecord[];
  onResetFilters: () => void;
  onApplyFilters: () => void;
}) => {
  const [submitting, setSubmitting] = useState(false);
  const [open, setOpen] = useState(false);
  const { setFiltersToLastSaved } = methods;

  const defaultTraders =
    (filters.find(
      (filter) => filter.name === ActivityFilterFields.traderPublicKey,
    )?.value as PartialAccount[]) || [];

  const defaultTokens =
    (filters.find(
      (filter) => filter.name === ActivityFilterFields.tokenPublicKey,
    )?.value as PartialAccount[]) || [];

  useEffect(() => {
    if (open) {
      setSubmitting(false);
      return;
    }

    if (!open && !submitting) {
      setFiltersToLastSaved();
    }
  }, [open, setFiltersToLastSaved, submitting]);

  return (
    <PopoverDrawer
      historyName="trade-activity-filters"
      onOpenChange={setOpen}
      open={open}
      title="Trade activity filters"
    >
      <PopoverDrawerTrigger asChild>
        <Button
          variant="link"
          className="flex items-center gap-1 pl-0 text-muted"
        >
          <span className="text-xs">Filter</span>
          <LuChevronDown />
        </Button>
      </PopoverDrawerTrigger>
      <TradeActivityFilterPopoverContent
        defaultTokens={defaultTokens}
        defaultTraders={defaultTraders}
        methods={methods}
        onApplyFilters={onApplyFilters}
        onResetFilters={onResetFilters}
        setSubmitting={setSubmitting}
      />
    </PopoverDrawer>
  );
};

const TradeActivityFilterPopoverContent = ({
  defaultTokens,
  defaultTraders,
  methods,
  onResetFilters,
  onApplyFilters,
  setSubmitting,
}: {
  defaultTokens: PartialAccount[];
  defaultTraders: PartialAccount[];
  methods: {
    findFilterByName: (name: string) => FilterRecord | undefined;
    updateFilter: (filter: FilterRecord, clearPrevFilters?: boolean) => void;
    removeFilter: <T extends string>(name: T) => void;
    isFilterActive: <T extends string>(name: T) => boolean;
    syncLastSavedFilters: () => void;
    setFiltersToLastSaved: () => void;
  };
  onResetFilters: () => void;
  onApplyFilters: () => void;
  setSubmitting: (submitting: boolean) => void;
}) => {
  const { syncLastSavedFilters } = methods;
  const { close } = usePopoverDrawerContext();

  const onSubmitClick = useCallback(() => {
    setSubmitting(true);
    syncLastSavedFilters();
    onApplyFilters();
    close();
  }, [onApplyFilters, close, syncLastSavedFilters, setSubmitting]);

  const onResetClick = useCallback(() => {
    setSubmitting(true);
    onResetFilters();
    close();
  }, [close, onResetFilters, setSubmitting]);

  return (
    <PopoverDrawerContent
      align="start"
      side="bottom"
      drawerDescription="Filter trade activity by token, trader, value, and time."
      className="
        custom-scrollbar w-screen overflow-y-auto p-6 pb-0
        shadow-xl md:max-h-[min(72vh,450px)] md:min-h-[450px]
        md:w-[600px] md:pb-6
      "
    >
      <Accordion type="single" collapsible>
        <div className="flex flex-col gap-5">
          <FiltersSet name="Tokens">
            <FilterMultiUser<ActivityFilterFields>
              {...methods}
              defaultUsers={defaultTokens}
              label="Public key or ticker"
              buttonClassName="h-[38px]"
              placeholder="Start typing a public key or ticker ..."
              name={ActivityFilterFields.tokenPublicKey}
              icon={<LuKeyRound />}
              pillsContainerRightPadding={150}
            />
          </FiltersSet>
          <FiltersSet name="Traders">
            <FilterMultiUser<ActivityFilterFields>
              {...methods}
              defaultUsers={defaultTraders}
              label="Public key or ticker"
              buttonClassName="h-[38px]"
              placeholder="Start typing a public key or ticker ..."
              name={ActivityFilterFields.traderPublicKey}
              icon={<LuKeyRound />}
            />
          </FiltersSet>
          <FiltersSet name="Trader Assets">
            <FilterNumeric<ActivityFilterFields>
              {...methods}
              name={ActivityFilterFields.traderAssetsUsd}
              label="Trader Assets USD"
              icon={<LuCircleDollarSign />}
            />
          </FiltersSet>
          <FiltersSet name="Trade Value">
            <FilterNumeric<ActivityFilterFields>
              {...methods}
              name={ActivityFilterFields.tradeValueUsd}
              label="Trade Value USD"
              icon={<LuCircleDollarSign />}
            />
          </FiltersSet>
          <FiltersSet name="Time">
            <FilterDate<ActivityFilterFields>
              {...methods}
              label="Time"
              icon={<LuCalendar />}
              name={ActivityFilterFields.tradeTimestamp}
            />
          </FiltersSet>
        </div>
        <PopoverFilterSortActions
          className="
            sticky inset-x-auto bottom-0 z-10 -mx-6 mt-4 w-auto
            rounded-none border-x-0 border-b-0 bg-card/95 backdrop-blur-sm
          "
          customSubmitButton={
            <Button variant="default" size="lg" onClick={onSubmitClick}>
              Apply
            </Button>
          }
          onResetClick={onResetClick}
        />
      </Accordion>
    </PopoverDrawerContent>
  );
};
